import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { applicationApi } from '@/api/endpoints'
import { ErrorState, Skeleton } from '@/components/ui/Feedback'
import { isUnavailable, retryUnlessMissing } from '@/features/mail/mailMeta'
import { cn, formatDate } from '@/lib/utils'
import type { Pipeline, PipelineColumn, PipelineColumnKey, PipelineItem } from '@/types/api'

import { CompanyAvatar } from './ApplicationsTable'
import { fromCurrent } from '@/features/applications/backTarget'

const COLUMN_ORDER: PipelineColumnKey[] = ['applied', 'ghosted', 'interviewing', 'rejected', 'offer']

/** Every colour here is a token class so the board reads the same in both themes. */
const COLUMN_META: Record<PipelineColumnKey, { label: string; dot: string; fill: string }> = {
  applied: { label: 'Applied', dot: 'bg-brand', fill: 'fill-brand' },
  ghosted: { label: 'Awaiting reply', dot: 'bg-ink-faint', fill: 'fill-ink-faint' },
  interviewing: { label: 'Interviewing', dot: 'bg-info', fill: 'fill-info' },
  rejected: { label: 'Rejected', dot: 'bg-danger', fill: 'fill-danger' },
  offer: { label: 'Offer', dot: 'bg-success', fill: 'fill-success' },
}

/** Normalise whatever the server sent into the five known columns, in order. */
function toColumns(pipeline: Pipeline | undefined): PipelineColumn[] {
  const byKey = new Map(
    (Array.isArray(pipeline?.columns) ? pipeline!.columns : []).map((column) => [column.key, column]),
  )
  return COLUMN_ORDER.map((key) => {
    const column = byKey.get(key)
    const items = Array.isArray(column?.items) ? column!.items : []
    return {
      key,
      label: column?.label || COLUMN_META[key].label,
      count: typeof column?.count === 'number' ? column.count : items.length,
      items,
    }
  })
}

export function PipelineView() {
  const navigate = useNavigate()
  const query = useQuery({
    queryKey: ['application-pipeline'],
    queryFn: applicationApi.pipeline,
    staleTime: 30_000,
    retry: retryUnlessMissing,
  })

  // Until the route ships, `/applications/pipeline` is read as an application id (422).
  const missing =
    isUnavailable(query.error) || (query.error instanceof ApiError && query.error.status === 422)
  if (query.isError && !missing) {
    return (
      <section className="card">
        <ErrorState description="We could not load your pipeline." onRetry={() => query.refetch()} />
      </section>
    )
  }

  const columns = toColumns(query.data)
  const open = (item: PipelineItem) => navigate(`/dashboard/applications/${item.applicationId}`, { state: fromCurrent() })

  return (
    <div className="space-y-4 sm:space-y-5">
      <SummaryLine columns={columns} loading={query.isLoading} />

      <section className="card px-4 py-4 sm:px-6 sm:py-5">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 className="text-[15px] font-semibold text-ink">Your hiring journey</h2>
          <p className="text-[12.5px] text-ink-muted">Updated from employer responses</p>
        </div>
        {query.isLoading ? <Skeleton className="h-[160px] w-full" /> : <FlowGraphic columns={columns} />}
      </section>

      {/* Phones: an edge-to-edge horizontal scroller of 80vw snap columns. lg+: five columns side by side. */}
      <div className="hscroll -mx-4 snap-x snap-mandatory scroll-pl-4 gap-3 px-4 pb-1 sm:-mx-6 sm:scroll-pl-6 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0 lg:pb-0">
        {columns.map((column) => (
          <KanbanColumn key={column.key} column={column} loading={query.isLoading} onOpen={open} />
        ))}
      </div>
    </div>
  )
}

/** "12 applications · 3 interviewing · 1 offer" */
function SummaryLine({ columns, loading }: { columns: PipelineColumn[]; loading: boolean }) {
  const count = (key: PipelineColumnKey) => columns.find((c) => c.key === key)?.count ?? 0
  const total = columns.reduce((sum, column) => sum + column.count, 0)
  const interviewing = count('interviewing')
  const offers = count('offer')
  if (loading) return <Skeleton className="h-4 w-56" />
  return (
    <p className="text-[13px] text-ink-muted">
      <Count n={total} /> {total === 1 ? 'application' : 'applications'}
      <Dot />
      <Count n={interviewing} tone={interviewing > 0 ? 'text-info' : undefined} /> interviewing
      <Dot />
      <Count n={offers} tone={offers > 0 ? 'text-success' : undefined} /> {offers === 1 ? 'offer' : 'offers'}
    </p>
  )
}

function Count({ n, tone }: { n: number; tone?: string }) {
  return <span className={cn('num font-semibold', tone ?? 'text-ink')}>{n}</span>
}

function Dot() {
  return <span className="mx-1.5 text-ink-faint">·</span>
}

// ---------------------------------------------------------------------------
// Flow graphic: one source ("Applied") fanning out to where things ended up.
// Pure HTML + SVG; band thickness is proportional to the count.
// ---------------------------------------------------------------------------
const FLOW_TARGETS: Array<{ key: PipelineColumnKey; label: string }> = [
  { key: 'interviewing', label: 'Interviewing' },
  { key: 'offer', label: 'Offer' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'ghosted', label: 'Ghosted' },
  { key: 'applied', label: 'Awaiting reply' },
]

function FlowGraphic({ columns }: { columns: PipelineColumn[] }) {
  // Current column counts, so the graphic always agrees with the board below.
  const countOf = (key: PipelineColumnKey) => columns.find((c) => c.key === key)?.count ?? 0
  const targets = FLOW_TARGETS.map((target) => ({ ...target, count: countOf(target.key) }))
  const total = targets.reduce((sum, target) => sum + target.count, 0)

  if (total === 0) {
    return (
      <div className="grid min-h-[120px] place-items-center rounded-field border border-dashed border-line px-4 py-6 text-center">
        <div>
          <p className="text-[14px] font-medium text-ink">Nothing to chart yet</p>
          <p className="mt-0.5 text-[13px] text-ink-muted">Apply to a few jobs and replies will show where each one went.</p>
        </div>
      </div>
    )
  }

  // Right-hand nodes get a minimum box so their labels never collide; the
  // band itself stays proportional and is centred in the box.
  const GAP = 8
  const MIN_BOX = 30
  const SCALE = 200
  const nodes: Array<(typeof targets)[number] & { top: number; box: number; thick: number }> = []
  let cursor = 0
  for (const target of targets) {
    const thick = (target.count / total) * SCALE
    const box = Math.max(MIN_BOX, thick)
    nodes.push({ ...target, top: cursor, box, thick })
    cursor += box + GAP
  }
  const height = cursor - GAP

  let leftCursor = (height - SCALE) / 2
  const bands = nodes
    .filter((node) => node.count > 0)
    .map((node) => {
      const l0 = leftCursor
      const l1 = leftCursor + node.thick
      leftCursor = l1
      const r0 = node.top + (node.box - node.thick) / 2
      const r1 = r0 + node.thick
      return {
        key: node.key,
        d: `M0 ${l0} C50 ${l0} 50 ${r0} 100 ${r0} L100 ${r1} C50 ${r1} 50 ${l1} 0 ${l1} Z`,
      }
    })

  return (
    <div
      className="flex items-stretch"
      style={{ height }}
      role="img"
      aria-label={`${total} applications: ${targets.map((t) => `${t.count} ${t.label.toLowerCase()}`).join(', ')}`}
    >
      <div className="flex w-[84px] shrink-0 items-center justify-end gap-2.5 sm:w-[120px]">
        <div className="text-right">
          <p className="num text-[22px] font-semibold leading-none text-ink">{total}</p>
          <p className="mt-1 text-[12.5px] text-ink-muted">Applied</p>
        </div>
        <div className="w-2.5 rounded-[3px] bg-ink/80" style={{ height: SCALE }} />
      </div>
      <svg className="h-full min-w-[40px] flex-1" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" aria-hidden="true">
        {bands.map((band) => (
          <path key={band.key} d={band.d} className={COLUMN_META[band.key].fill} fillOpacity={0.28} />
        ))}
      </svg>
      <div className="relative w-[124px] shrink-0 sm:w-[170px]">
        {nodes.map((node) => (
          <div key={node.key} className="absolute left-0 right-0 flex items-center gap-2.5" style={{ top: node.top, height: node.box }}>
            <div
              className={cn('w-2.5 shrink-0 rounded-[3px]', node.count > 0 ? COLUMN_META[node.key].dot : 'bg-line-strong')}
              style={{ height: Math.max(node.count > 0 ? 3 : 2, node.thick) }}
            />
            <p className="min-w-0 truncate text-[13px] text-ink-soft">
              <span className="num font-semibold text-ink">{node.count}</span> {node.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kanban
// ---------------------------------------------------------------------------
function KanbanColumn({
  column,
  loading,
  onOpen,
}: {
  column: PipelineColumn
  loading: boolean
  onOpen: (item: PipelineItem) => void
}) {
  const meta = COLUMN_META[column.key]
  return (
    <section
      aria-label={column.label}
      className="flex max-h-[min(620px,calc(100dvh-240px))] w-[80vw] shrink-0 snap-start flex-col overflow-y-auto rounded-card border border-line bg-surface-muted md:w-[300px] lg:w-auto lg:min-w-0"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-surface-muted px-3.5 pb-2 pt-3">
        <h3 className="flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
          <span className={cn('h-2 w-2 rounded-full', meta.dot)} />
          {column.label}
        </h3>
        <span className="num rounded-pill bg-surface px-2 py-0.5 text-[12px] font-medium text-ink-muted ring-1 ring-line">
          {column.count}
        </span>
      </header>
      <div className="space-y-2 px-2.5 pb-2.5">
        {loading ? (
          <>
            <Skeleton className="h-[92px] w-full rounded-field" />
            <Skeleton className="h-[92px] w-full rounded-field" />
          </>
        ) : column.items.length === 0 ? (
          <p className="rounded-field border border-dashed border-line px-3 py-6 text-center text-[13px] text-ink-faint">
            No applications
          </p>
        ) : (
          column.items.map((item) => <KanbanCard key={item.applicationId} item={item} onOpen={() => onOpen(item)} />)
        )}
      </div>
    </section>
  )
}

function KanbanCard({ item, onOpen }: { item: PipelineItem; onOpen: () => void }) {
  const emails = item.emailCount ?? 0
  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full rounded-field border border-line bg-surface p-3 text-left shadow-card transition hover:-translate-y-px hover:border-line-strong"
    >
      <div className="flex items-center gap-2.5">
        <CompanyAvatar name={item.companyName || '?'} logoUrl={item.logoUrl} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium leading-snug text-ink">{item.companyName}</p>
          <p className="truncate text-[12.5px] text-ink-muted">{item.title}</p>
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 text-[12px] text-ink-faint">
        <span className="num">{item.appliedAt ? formatDate(item.appliedAt) : 'Not sent'}</span>
        <span className={cn('inline-flex items-center gap-1', emails > 0 ? 'text-ink-muted' : 'text-ink-faint')}>
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="m3 5.5 7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
          <span className="num">{emails}</span> {emails === 1 ? 'email' : 'emails'}
        </span>
      </div>
    </button>
  )
}
